import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import _ from 'lodash';
import EditInline from 'ui-component/EditInline';

import {
  CustomPropertyDataType,
  CustomPropertyModel,
  CustomPropertyValue,
  CustomPropertyVisibility,
} from 'types/customProperty';
import useGetCustomPropertyDefinitions from 'hooks/useGetCustomPropertyDefinitions';
import { editInlineComponentMap } from 'ui-component/DetailCustomProperties';
import SidebarInfoSection from 'ui-component/DetailLayout/SidebarComponents/SidebarInfoSection';
import { useGetOrgUsersQuery } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'store';
import { GenericResource, PatchMutation } from 'types/api';
import { InfoSectionItemFormatTypes } from 'ui-component/InfoSectionItem';

interface SidebarCustomPropertiesPropsClientV2<
  T extends GenericResource,
  K extends GenericResource
> {
  submitAsClientV2Api: true;
  useMutation: PatchMutation<T>;
  record: K | null | undefined;
  model: CustomPropertyModel;
  isLoadingRecord: boolean;
  disableEdit?: boolean;
  idProp?: string;
  otherProps?: object;
}

interface SidebarCustomPropertiesPropsApiV1<
  T extends GenericResource,
  K extends GenericResource
> {
  submitAsClientV2Api: false;
  // the `any` here seems to be the type used in the RTK Query type definitions
  useMutation: UseMutation<any>;
  record: K | null | undefined;
  model: CustomPropertyModel;
  isLoadingRecord: boolean;
  disableEdit?: boolean;
  idProp?: string;
  otherProps?: object;
}

type SidebarCustomPropertiesProps<
  T extends GenericResource,
  K extends GenericResource
> =
  | SidebarCustomPropertiesPropsApiV1<T, K>
  | SidebarCustomPropertiesPropsClientV2<T, K>;

const SidebarCustomProperties = <
  T extends GenericResource,
  K extends GenericResource = T
>({
  useMutation,
  record,
  model,
  isLoadingRecord,
  disableEdit,
  idProp = 'id',
  otherProps = {},
  submitAsClientV2Api = false,
}: SidebarCustomPropertiesProps<T, K>) => {
  const { activeOrgId } = useSelector((state) => state.org);

  const { customProperties, isFetching } = useGetCustomPropertyDefinitions({
    model,
    visibilityContext: CustomPropertyVisibility.DETAIL_SIDEBAR,
  });

  const hasUserCustomProp = _.some(
    customProperties,
    (f) => f.dataType === CustomPropertyDataType.USER
  );

  const { data: users = [], isLoading: isLoadingUsers } = useGetOrgUsersQuery(
    hasUserCustomProp && activeOrgId ? activeOrgId : skipToken
  );

  if (customProperties.length < 1) {
    return <></>;
  }

  return (
    <>
      {customProperties.map((f) => {
        const InlineComponent = editInlineComponentMap[f.dataType];
        const otherInlineComponentProps =
          f.dataType === CustomPropertyDataType.SINGLE_CHOICE
            ? {
                mb: 0,
                options: _.sortBy(f.values, 'label'),
                getOptionLabel: (option: CustomPropertyValue) => option.label,
                initialValueMatcher: (value: string) =>
                  _.find(f.values, (option) => option.value === value) ?? null,
                valueFromOption: (option: CustomPropertyValue) => option.value,
              }
            : f.dataType === CustomPropertyDataType.MULTI_CHOICE
            ? {
                options: _.sortBy(f.values, 'label'),
              }
            : {};

        const recordCustomProperties = _.get(record, 'customProperties', {});

        const valueForProp = _.get(
          recordCustomProperties,
          f.key,
          f.defaultValue
        );

        const isUserField = f.dataType === CustomPropertyDataType.USER;

        const getLabelForPropValue = () => {
          if (isUserField) {
            const user = _.find(users, (u) => u.id === valueForProp);
            return user ? `${user.firstName} ${user.lastName}` : valueForProp;
          }
          return _.find(f.values, (option) => option.value === valueForProp)
            ?.label;
        };

        const formatType =
          f.dataType === CustomPropertyDataType.BOOLEAN
            ? InfoSectionItemFormatTypes.boolean
            : f.dataType === CustomPropertyDataType.URL
            ? InfoSectionItemFormatTypes.url
            : f.dataType === CustomPropertyDataType.RICH_TEXT
            ? InfoSectionItemFormatTypes.richText
            : undefined;

        return (
          <SidebarInfoSection
            key={f.key}
            caption={f.name}
            isLoading={
              isLoadingRecord || isFetching || (isUserField && isLoadingUsers)
            }
            value={getLabelForPropValue() ?? valueForProp}
            disableEdit={disableEdit}
            formatType={formatType}
            editInline={
              <EditInline
                useMutation={useMutation}
                name={f.key}
                label={f.name}
                hideLabel
                value={_.get(recordCustomProperties, f.key, f.defaultValue)}
                id={_.get(record, 'id', '') as string}
                preSubmit={(data) => ({
                  customProperties: {
                    [f.key]: data,
                  },
                })}
                idProp={idProp}
                otherProps={otherProps}
                submitAsClientV2Api={submitAsClientV2Api}
                fullHeight={f.dataType === CustomPropertyDataType.RICH_TEXT}
              >
                <InlineComponent
                  disableRequiredProps
                  {...otherInlineComponentProps}
                />
              </EditInline>
            }
          />
        );
      })}
    </>
  );
};

export default SidebarCustomProperties;
